import React, {
  FC, ReactNode, useCallback, useEffect, useRef, useState,
  TransitionEventHandler, useLayoutEffect,
} from 'react';

import classNames from 'classnames';

import { useBreakpoint } from '@ast/magma/breakpoints';

import {
  HorizontalBannerWidgetZone,
} from '@app/common/components/HorizontalBannerWidgetZone/HorizontalBannerWidgetZone';
import { VerticalBannerWidgetZone } from '@app/common/components/VerticalBannerWidgetZone/VerticalBannerWidgetZone';
import { throttle } from '@app/common/utils';
import { useResizeObserver } from '@app/common/utils/hooks/useResizeObserver';

import { ZoneName } from '@app/core/components/WidgetZone/constants';

import { TwinColumnLayoutHorizontalZone } from './TwinColumnLayoutHorizontalZone/TwinColumnLayoutHorizontalZone';

import styles from './TwinColumnLayout.pcss';

const RIGHT_BANNER_WIDTH = '--right-banner-width';

/**
 * Main layout component properties
 */
export interface TwinColumnLayoutProps {
  /*
    Class name to apply custom styling.
    @default undefined.
   */
  readonly className?: string;

  /**
   * Class name to apply custom styling to content section.
   */
  readonly contentClassName?: string;
  /**
   * If 'true' then selected item section will overlap menu section on mobile layout
   */
  readonly secondChildActive?: boolean;
  /**
   * Two children elements: menu section and selected item section
   */
  readonly children: [ReactNode, ReactNode];
  /**
   * Fired when layout transition animation ends.
   */
  readonly onTransitionEnd?: () => void;
}

/**
 * TwinColumnLayout is a container with two children: menu section and selected item section.
 * On desktop these children stack together side by side with 1:2 proportion.
 * On mobile only menu selection is visible by default but
 * when user select something from menu then selected item section slide-in on top of menu section.
 */
// eslint-disable-next-line import/prefer-default-export
export const TwinColumnLayout: FC<TwinColumnLayoutProps> = (
  {
    className, contentClassName, children, secondChildActive, onTransitionEnd,
  },
) => {
  const isMobile = useBreakpoint().device === 'mobile';
  const isPortrait = useBreakpoint().orientation === 'portrait';
  const [transitionInProgress, setTransitionInProgress] = useState<boolean>(false);
  const [firstChildrenScrollPosition, setFirstChildrenScrollPosition] = useState<number>(0);
  const secondChildActiveInner = useRef<boolean | undefined>(undefined);
  const [withBackgroundImage, setWithBackgroundImage] = useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const asideBannerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isMobile) {
      window.scrollTo({ top: 0 });
    }
  }, [isMobile]);

  /**
   * Observe aside widget size changes and set new value as custom css variable
   * to be able to shift header and footer banner background on same value.
   */
  useResizeObserver((entry) => {
    const asideBanner = entry.target as HTMLElement;

    if (containerRef.current && asideBanner) {
      containerRef.current.style.setProperty(RIGHT_BANNER_WIDTH, `${asideBanner.offsetWidth}px`);
    }
  }, asideBannerRef);

  /* scroll to saved position after first children is shown */
  useEffect(() => {
    if (isMobile) {
      if (!secondChildActive) {
        window.scrollTo({ top: 0 });
        // Next scroll position
        setImmediate(() => {
          window.scrollTo({
            top: firstChildrenScrollPosition,
            behavior: 'smooth',
          });
        });
      }
    }
  }, [secondChildActive]);

  // Raise with backround image flag
  useEffect(() => {
    const main = document.querySelector('main');
    if (main) {
      setWithBackgroundImage(getComputedStyle(main, ':before').backgroundImage !== 'none');
    }
  }, []);

  /* memorize scroll position of first children */
  useLayoutEffect(() => {
    const handleScroll = throttle(() => {
      setFirstChildrenScrollPosition(document.documentElement.scrollTop);
    }, 100);

    if (isMobile && !secondChildActive) {
      window.addEventListener('scroll', handleScroll);
    }
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isMobile, secondChildActive]);

  // On mobile if 'is content selected' is set then
  // raise transition started flag to render both parts on transiton
  if (isMobile
    && typeof secondChildActiveInner.current !== 'undefined'
    && !!secondChildActive !== secondChildActiveInner.current
    && !transitionInProgress) {
    setTransitionInProgress(true);
  }

  const handleTransitionEnd: TransitionEventHandler<HTMLElement> = useCallback((event) => {
    if (event.propertyName === 'transform') {
      setTransitionInProgress(false);
      if (onTransitionEnd) {
        onTransitionEnd();
      }
    }
  }, [transitionInProgress]);

  // Save previous 'is content selected' value
  secondChildActiveInner.current = !!secondChildActive;

  const showBannersFirstZone = isMobile && (!secondChildActive || transitionInProgress);
  const showBannersSecondZone = isMobile && (secondChildActive || transitionInProgress);

  return (
    <div
      ref={containerRef}
      data-stable-name="TwinColumnLayoutContainer"
      className={classNames(
        className,
        styles.container,
        withBackgroundImage && styles.withBackgroundImage,
      )}
    >
      {!isMobile && (
        <TwinColumnLayoutHorizontalZone zoneName={ZoneName.HeaderBanner} />
      )}
      <div className={styles.row}>
        <div className={classNames(styles.leftPadding)} />
        <div className={styles.centerContainer}>
          {!isMobile && (
            <TwinColumnLayoutHorizontalZone zoneName={ZoneName.PreMiddle} />
          )}
          <div
            className={classNames(
              styles.main,
              secondChildActive && styles.selected,
              !transitionInProgress && styles.transitionEnd,
            )}
          >
            <section
              data-stable-name="TwinColumnLayoutSection"
              aria-hidden={isMobile && secondChildActive}
              onTransitionEnd={handleTransitionEnd}
              className={classNames(styles.selector)}
            >
              {showBannersFirstZone && (
                <>
                  <HorizontalBannerWidgetZone
                    className={styles.headerMobileBannerZone}
                    zoneName={ZoneName.HeaderBanner}
                  />
                  <HorizontalBannerWidgetZone
                    zoneName={ZoneName.PreMiddle}
                  />
                </>
              )}
              {children[0]}
              {showBannersFirstZone && (
                <>
                  <HorizontalBannerWidgetZone
                    zoneName={ZoneName.PostMiddle}
                  />
                  <HorizontalBannerWidgetZone
                    className={styles.footerMobileBannerZone}
                    zoneName={ZoneName.FooterBanner}
                  />
                </>
              )}
            </section>
            <section
              data-stable-name="TwinColumnLayoutSection"
              aria-hidden={isMobile && !secondChildActive}
              className={classNames(styles.content, contentClassName)}
            >
              {showBannersSecondZone && (
                <HorizontalBannerWidgetZone
                  className={styles.headerMobileBannerZone}
                  zoneName={ZoneName.PreMiddle}
                />
              )}
              {children[1]}
              {showBannersSecondZone && (
                <HorizontalBannerWidgetZone
                  className={styles.footerMobileBannerZone}
                  zoneName={ZoneName.PostMiddle}
                />
              )}
            </section>
          </div>
          {!isMobile && (
            <TwinColumnLayoutHorizontalZone zoneName={ZoneName.PostMiddle} />
          )}
        </div>
        {!(isMobile || isPortrait) && (
          <VerticalBannerWidgetZone
            ref={asideBannerRef}
            className={styles.rightContent}
          />
        )}
        <div className={classNames(styles.rightPadding)} />
      </div>
      {!isMobile && (
        <TwinColumnLayoutHorizontalZone zoneName={ZoneName.FooterBanner} />
      )}
    </div>
  );
};
